import React from "react";
import {
  ShieldCheck, Star, Briefcase, Clock, FileCheck, Medal, ThumbsUp, Award, TrendingUp, Zap,
  MessageSquare, Megaphone, Terminal,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import {
  BADGE_REGISTRY, FAMILY_ORDER, FIXED_CHIP, BADGE_TONE_CLASSES, badgeTone, badgeLabel,
  badgeTooltip, badgeDashed, groupBadges, cardBadges,
} from "../lib/badge_presets";

const ICONS = {
  "shield-check": ShieldCheck, star: Star, briefcase: Briefcase, clock: Clock,
  "file-check": FileCheck, medal: Medal, "thumbs-up": ThumbsUp, award: Award,
  "trending-up": TrendingUp, zap: Zap, "message-square": MessageSquare,
  megaphone: Megaphone, terminal: Terminal, "listings-diamond": DiamondGlyph,
};

// The Listings-milestone "⬖" glyph, sized like a lucide line icon so it sits inside the chip.
function DiamondGlyph({ className }) {
  return (
    <span className={className} aria-hidden="true"
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px", lineHeight: 1 }}>
      ⬖
    </span>
  );
}

// BETA TESTER — terminal treatment (locked spec). CSS-only, no images/canvas. A ">" prompt,
// the snake_case label, and a blinking block cursor that goes solid under prefers-reduced-motion.
function BetaTesterChip({ withTooltip = true }) {
  const chip = (
    <span
      data-testid="badge-beta_tester"
      className="inline-flex items-center font-mono cursor-default"
      style={{
        background: "#07100F", border: "1px solid #1c5e55", color: "#2DE1C2",
        borderRadius: 3, padding: "7px 11px", fontSize: 10, letterSpacing: "1.2px", lineHeight: 1,
      }}
    >
      <span style={{ color: "#1c8a78", marginRight: 4 }} aria-hidden="true">&gt;</span>
      beta_tester
      <span className="os-beta-cursor" aria-hidden="true" />
    </span>
  );
  if (!withTooltip) return chip;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent className="max-w-[240px] font-body text-xs normal-case tracking-normal leading-snug">
          {BADGE_REGISTRY.beta_tester.tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ONE badge component, two sizes. Locked visual: filled chip, thin border, monospace uppercase
// label at 1.2px tracking, 10px line icon. Verification + recognition badges use a FIXED identity
// colour (green ID / blue Dealer+FFL / gold Founding / burnt-orange Ambassador); reputation
// badges take the account tone. Beta Tester renders in the terminal style above.
export function Badge({ badge, accountTone = "green", size = "full" }) {
  if (badge.id === "beta_tester") return <BetaTesterChip />;
  const meta = BADGE_REGISTRY[badge.id];
  if (!meta) return null;
  const Icon = ICONS[meta.icon] || ShieldCheck;
  const isDashed = badgeDashed(badge);
  const fixed = !isDashed ? FIXED_CHIP[badge.id] : null;
  const tone = badgeTone(badge.id, accountTone);
  const toneCls = BADGE_TONE_CLASSES[tone] || BADGE_TONE_CLASSES.green;
  const dashed = isDashed ? "border-dashed opacity-70" : "";
  const compact = size === "compact";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`badge-${badge.id}`}
            style={fixed ? { backgroundColor: fixed.bg, color: fixed.text, ...(fixed.border ? { borderColor: fixed.border } : {}) } : undefined}
            className={`inline-flex items-center gap-1 border font-mono uppercase tracking-[1.2px] leading-none cursor-default ${fixed ? (fixed.border ? "" : "border-transparent") : `${toneCls} ${dashed}`} ${compact ? "px-2 py-[3px] text-[9px]" : "px-2.5 py-[5px] text-[10px]"}`}
          >
            <Icon className="w-2.5 h-2.5 shrink-0" strokeWidth={2} />
            {badgeLabel(badge, compact ? "compact" : "full")}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px] font-body text-xs normal-case tracking-normal leading-snug">
          {badgeTooltip(badge)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Full set on profiles; card mode caps at 3. Two families always render as SEPARATE clusters
// with a visible gap so the verification (safety) cluster is read first.
export function BadgeRow({ badges, accountTone = "green", mode = "full", className = "" }) {
  // The founding seat is now shown as the standalone gold/blue FoundingSeatChip (split by
  // account type), so it is filtered out of the generic badge shelf to avoid a duplicate.
  // "pro" is filtered too: it must never appear on any BUYER-facing surface (cards, buy box,
  // public profile, search, messages). A seller still sees their Pro membership in their own
  // account/billing view (Settings badge-progress + membership tier), not through this row.
  const list = (badges || []).filter((b) => b.id !== "founding_member" && b.id !== "pro");
  if (!list.length) return null;

  if (mode === "card") {
    const capped = cardBadges(list);
    if (!capped.length) return null;
    return (
      <div data-testid="badge-row" className={`flex flex-wrap items-center gap-1.5 ${className}`}>
        {capped.map((b) => <Badge key={b.id} badge={b} accountTone={accountTone} size="compact" />)}
      </div>
    );
  }

  // Full shelf: grouped by family, each family its own cluster, visible gap between clusters.
  const groups = groupBadges(list);
  const clusters = FAMILY_ORDER.filter((fam) => groups[fam] && groups[fam].length);
  return (
    <div data-testid="badge-row" className={`flex flex-wrap items-center gap-x-4 gap-y-2 ${className}`}>
      {clusters.map((fam) => (
        <div key={fam} data-testid={`badge-cluster-${fam}`} className="flex flex-wrap items-center gap-1.5">
          {groups[fam].map((b) => <Badge key={b.id} badge={b} accountTone={accountTone} size="full" />)}
        </div>
      ))}
    </div>
  );
}

// Specialty "Shoots" — deliberately NOT badges. Outline, no fill, body typeface, muted.
// Its own row, never in the badge row, never on cards.
export function SpecialtyRow({ tags, className = "" }) {
  if (!tags || !tags.length) return null;
  return (
    <div data-testid="specialty-row" className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Shoots</span>
      {tags.map((t) => (
        <span
          key={t}
          data-testid={`specialty-${t}`}
          className="inline-flex items-center rounded-full border border-muted-foreground/40 text-muted-foreground font-body text-xs px-3 py-0.5"
        >
          {t}
        </span>
      ))}
    </div>
  );
}
